import type { InjectionKey } from "vue";
import type { TurnAttachment } from "../api/turn-attachments";

export interface ChatDocument {
	name: string;
	content: string;
	kind: "markdown" | "html" | "text" | "csv" | "pdf";
	status: "writing" | "ready" | "failed";
	callId?: string;
}

export const OPEN_CHAT_FILE: InjectionKey<(file: TurnAttachment) => void> =
	Symbol("open-chat-file");
export const DOCUMENT_LIMIT = 2 * 1024 * 1024;

export function documentKind(name: string, mime = ""): ChatDocument["kind"] | null {
	if (/\.pdf$/i.test(name) || mime.split(";")[0] === "application/pdf") return "pdf";
	if (/\.(csv|tsv)$/i.test(name) || /^(text\/(csv|tab-separated-values))(;|$)/.test(mime))
		return "csv";
	if (/\.html?$/i.test(name) || mime.split(";")[0] === "text/html") return "html";
	if (/\.md$/i.test(name) || mime.split(";")[0] === "text/markdown") return "markdown";
	if (
		/\.(txt|json|csv|css|js|ts)$/i.test(name) ||
		/^(text\/plain|application\/json)(;|$)/.test(mime)
	)
		return "text";
	return null;
}

// Read top-level strings even while the final string is still arriving. Never guess nested fields.
function partialStrings(raw: string): Record<string, string> {
	const values: Record<string, string> = {};
	let i = raw.indexOf("{") + 1;
	if (i !== 1) return values;
	while (i < raw.length) {
		while (/[\s,]/.test(raw[i] ?? "") && i < raw.length) i++;
		if (raw[i] !== '"') break;
		const key = readString(raw, i);
		if (!key.closed) break;
		i = key.end;
		while (/\s/.test(raw[i] ?? "") && i < raw.length) i++;
		if (raw[i++] !== ":") break;
		while (/\s/.test(raw[i] ?? "") && i < raw.length) i++;
		if (raw[i] !== '"') break;
		const value = readString(raw, i);
		values[key.text] = value.text;
		i = value.end;
		if (!value.closed) break;
	}
	return values;
}

function readString(raw: string, start: number): { text: string; end: number; closed: boolean } {
	let text = "";
	let i = start + 1;
	for (; i < raw.length; i++) {
		const c = raw[i];
		if (c === '"') return { text, end: i + 1, closed: true };
		if (c !== "\\") {
			text += c;
			continue;
		}
		const escaped = raw[++i];
		if (!escaped) break;
		if (escaped === "u") {
			const hex = raw.slice(i + 1, i + 5);
			if (!/^[\da-f]{4}$/i.test(hex)) break;
			text += String.fromCharCode(Number.parseInt(hex, 16));
			i += 4;
		} else {
			const decoded: Record<string, string> = {
				n: "\n",
				r: "\r",
				t: "\t",
				b: "\b",
				f: "\f",
				'"': '"',
				"\\": "\\",
				"/": "/",
			};
			if (!(escaped in decoded)) break;
			text += decoded[escaped];
		}
	}
	return { text, end: i, closed: false };
}

export function writingDocument(tool: string, input: unknown): ChatDocument | null {
	if (tool !== "files") return null;
	let fields: Record<string, unknown>;
	if (typeof input === "string") {
		if (input.length > DOCUMENT_LIMIT) return null;
		try {
			fields = JSON.parse(input);
		} catch {
			fields = partialStrings(input);
		}
	} else if (input && typeof input === "object" && !Array.isArray(input)) {
		fields = input as Record<string, unknown>;
	} else return null;
	if (
		fields?.op !== "write" ||
		typeof fields.path !== "string" ||
		typeof fields.content !== "string"
	)
		return null;
	const kind = documentKind(fields.path);
	if (!kind || kind === "pdf" || fields.content.length > DOCUMENT_LIMIT) return null;
	return { name: fields.path, content: fields.content, kind, status: "writing" };
}

export function isolatedHtml(content: string): string {
	// The sandbox isolates the panel; CSP allows asset loading but blocks fetch, forms and arbitrary script hosts.
	return (
		`<!doctype html><meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' https://cdn.jsdelivr.net https://unpkg.com https://cdnjs.cloudflare.com; style-src 'unsafe-inline' https:; img-src data: blob: https:; font-src data: https:; media-src data: blob: https:; connect-src 'none'; form-action 'none'; base-uri 'none'"><meta name="referrer" content="no-referrer">` +
		`<script>document.addEventListener('mouseup',()=>parent.postMessage({type:'ben-document-selection',text:String(getSelection()).slice(0,4000)},'*'));document.addEventListener('keyup',()=>parent.postMessage({type:'ben-document-selection',text:String(getSelection()).slice(0,4000)},'*'));</script>` +
		content
	);
}

/** Successful writes already travel in persisted turn steps, including edits and restores. */
export function documentPaths(
	steps: readonly {
		isError?: boolean;
		note?: { kind: string; subject?: string; path?: string };
		input?: string;
	}[],
): string[] {
	return [
		...new Set(
			steps
				.filter((step) => !step.isError && step.note?.kind === "file.write")
				.map((step) => {
					if (step.note?.path) return step.note.path;
					if (step.input) {
						try {
							const fields = JSON.parse(step.input);
							if (typeof fields?.path === "string") return fields.path;
						} catch {
							const fields = partialStrings(step.input);
							if (fields.path) return fields.path;
						}
					}
					return step.note?.subject ?? "";
				})
				.filter((path) => path.startsWith("/") && documentKind(path)),
		),
	];
}

export function changedDocumentPath(tool: string, input: unknown): string | null {
	if (tool !== "files" || !input || typeof input !== "object") return null;
	const fields = input as Record<string, unknown>;
	return ["write", "edit", "restore"].includes(String(fields.op)) &&
		typeof fields.path === "string" &&
		documentKind(fields.path)
		? fields.path
		: null;
}

export interface DocumentEdits {
	path: string;
	edits: Array<{ find: string; replace: string }>;
}
export function documentEdits(tool: string, input: unknown): DocumentEdits | null {
	if (tool !== "files") return null;
	let fields: Record<string, unknown>;
	if (typeof input === "string") {
		if (input.length > DOCUMENT_LIMIT) return null;
		try {
			fields = JSON.parse(input);
		} catch {
			fields = partialEditFields(input);
		}
	} else if (input && typeof input === "object") fields = input as Record<string, unknown>;
	else return null;
	if (fields?.op !== "edit" || typeof fields.path !== "string" || !Array.isArray(fields.edits))
		return null;
	const kind = documentKind(fields.path);
	if (!kind || kind === "pdf") return null;
	const edits = fields.edits.filter(
		(edit): edit is { find: string; replace: string } =>
			typeof edit?.find === "string" && !!edit.find && typeof edit.replace === "string",
	);
	return edits.length ? { path: fields.path, edits: edits.slice(0, 50) } : null;
}
function partialEditFields(input: string): Record<string, unknown> {
	const fields: Record<string, unknown> = partialStrings(input);
	const array = input.match(/"edits"\s*:\s*\[/);
	const edits: Array<Record<string, string>> = [];
	if (array?.index !== undefined) {
		let i = array.index + array[0].length;
		while (i < input.length && edits.length < 50) {
			while (/[\s,]/.test(input[i] ?? "") && i < input.length) i++;
			if (input[i] !== "{") break;
			edits.push(partialStrings(input.slice(i)));
			i = editEnd(input, i);
		}
	}
	return { ...fields, edits };
}
function editEnd(raw: string, start: number): number {
	let i = start + 1;
	while (i < raw.length) {
		if (raw[i] === '"') {
			const string = readString(raw, i);
			if (!string.closed) return raw.length;
			i = string.end;
		} else if (raw[i++] === "}") return i;
	}
	return raw.length;
}
/** Preview only; the server validates and saves the authoritative result. */
export function applyDocumentEdits(base: string, edits: DocumentEdits["edits"]): string | null {
	let text = base;
	for (const edit of edits) {
		const at = text.indexOf(edit.find);
		if (at < 0 || text.indexOf(edit.find, at + edit.find.length) >= 0) return null;
		text = text.slice(0, at) + edit.replace + text.slice(at + edit.find.length);
		if (text.length > DOCUMENT_LIMIT) return null;
	}
	return text;
}
